import { useEffect, useRef, useState, useMemo, type FormEvent } from 'react';
import { StatusCard } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useTranslation } from '@/hooks/useTranslation';
import { useApiClient } from '@/hooks/useApiClient';
import { TraceabilityApiClient } from '../api/TraceabilityApiClient';
import type { ToolResponse } from '../api/TraceabilityResponseType';
import type { Result } from '@/lib/http/HttpClient';
import { cn } from '@/lib/utils';
import { useToolSummaries } from '../hooks/useToolSummaries';
import { toolWriteErrorMessage } from '../utils/toolForm';
import { ExistingToolIds } from './pickers/ExistingToolIds';
import { takenToolId } from './pickers/pickerHelpers';
import { ReferencePicker } from './pickers/ReferencePicker';
import { CompanyField } from './CompanyField';

type CreateToolFormProps = {
  companyName: string;
  companyAutoFilled: boolean;
  /** ID to start from ("Créer cet outil" on an unknown ID of the trace entry). */
  initialToolId?: string;
  onSuccess?: (toolId: string) => void;
};

export const CreateToolForm: React.FC<CreateToolFormProps> = ({
  companyName,
  companyAutoFilled,
  initialToolId,
  onSuccess,
}) => {
  const http = useApiClient();
  const { t } = useTranslation();
  const { summaries, refresh: refreshSummaries } = useToolSummaries();
  const traceabilityApiClient = useMemo(
    () => (http ? new TraceabilityApiClient(http) : null),
    [http]
  );

  const [toolId, setToolId] = useState(initialToolId ?? '');
  const [reference, setReference] = useState('');
  const [company, setCompany] = useState(companyName);
  const [result, setResult] = useState<Result<ToolResponse> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const submitControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (companyAutoFilled) setCompany(companyName);
  }, [companyName, companyAutoFilled]);

  useEffect(() => {
    if (initialToolId !== undefined) setToolId(initialToolId);
  }, [initialToolId]);

  const taken = takenToolId(toolId, summaries);

  useEffect(() => {
    return () => {
      submitControllerRef.current?.abort();
    };
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!traceabilityApiClient || taken) return;
    const newToolId = toolId.trim();

    submitControllerRef.current?.abort();
    const ac = new AbortController();
    submitControllerRef.current = ac;

    setResult(null);
    setIsLoading(true);

    try {
      const res = await traceabilityApiClient.toolwrite(
        {
          tool: {
            toolId: newToolId,
            reference: reference.trim(),
            companyName: company,
            traces: [],
          },
        },
        { signal: ac.signal }
      );
      if (ac.signal.aborted) return;
      setResult(
        res.ok
          ? res
          : {
              ...res,
              message: toolWriteErrorMessage(
                res,
                t,
                'traceability.workshop.toolForm.errors.default'
              ),
            }
      );
      if (res.ok) {
        setToolId('');
        refreshSummaries();
        onSuccess?.(newToolId);
      }
    } catch {
      if (ac.signal.aborted) return;
      setResult({
        ok: false,
        code: 0,
        name: 'NetworkError',
        timestamp: new Date().toISOString(),
        message: t('traceability.workshop.toolForm.errors.default'),
      });
    } finally {
      if (!ac.signal.aborted) setIsLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="grid items-start gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="create-toolId">
            {t('traceability.workshop.toolForm.fields.toolId.label')}
          </Label>
          <Input
            id="create-toolId"
            type="text"
            placeholder={t('traceability.workshop.toolForm.fields.toolId.placeholder')}
            value={toolId}
            onChange={(e) => setToolId(e.target.value)}
            aria-invalid={taken ? true : undefined}
            aria-describedby="create-toolId-existing"
            className={cn('font-mono', taken && 'border-destructive')}
            required
          />
          <ExistingToolIds
            id="create-toolId-existing"
            value={toolId}
            summaries={summaries}
            onUse={setToolId}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="create-reference">
            {t('traceability.workshop.toolForm.fields.reference.label')}
          </Label>
          <ReferencePicker
            id="create-reference"
            value={reference}
            onChange={setReference}
            summaries={summaries}
            placeholder={t('traceability.workshop.toolForm.fields.reference.placeholder')}
            required
          />
        </div>
      </div>
      <CompanyField
        id="create-company"
        value={company}
        onChange={setCompany}
        autoFilled={companyAutoFilled}
      />
      <div className="flex flex-col gap-3">
        <Button type="submit" className="w-full" loading={isLoading} disabled={taken !== null}>
          {t('traceability.workshop.toolForm.submit.single')}
        </Button>
        {result?.ok && (
          <StatusCard
            type="success"
            message={t('traceability.workshop.toolForm.success')}
            className="self-stretch"
          />
        )}
        {result && !result.ok && (
          <StatusCard
            type="error"
            message={result.message ?? t('traceability.workshop.toolForm.errors.default')}
            className="self-stretch"
          />
        )}
      </div>
    </form>
  );
};
